import dayjs from "dayjs";
import cls from "classnames";
import { useEffect, useRef } from "react";
import { useMessage } from "@/contexts";
import { DecimalHelper } from "@/helpers";
import { TransferHelper } from "@/domains/transfer";
import { useAppDispatch, useAppSelector } from "@/stores";
import { UserRead, UserStatus } from "@/domains/user";
import {
  operatorManageTenantSelects,
  operatorManageTenantThunks,
} from "./store";
// components
import { Button, Popover, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { CreateModal } from "./CreateModal";
import { UpdateModal, UpdateModalRef } from "./UpdateModal";

export const OperatorManageTenantsPage = () => {
  const updateModalRef = useRef<UpdateModalRef>(null);

  const dispatch = useAppDispatch();

  const loading = useAppSelector(operatorManageTenantSelects.loading);

  const queryParams = useAppSelector(operatorManageTenantSelects.queryParams);

  const tenants = useAppSelector(operatorManageTenantSelects.tenants);

  const { messageApi } = useMessage();

  useEffect(() => {
    void dispatch(operatorManageTenantThunks.list(queryParams));
  }, [dispatch, queryParams]);

  const handleCreateModalCreate = () => {
    void messageApi.success("商戶新增成功！");
    void dispatch(operatorManageTenantThunks.list(queryParams));
  };

  const handleUpdateModalUpdate = () => {
    void dispatch(operatorManageTenantThunks.list(queryParams));
  };

  const columns: ColumnsType<UserRead> = [
    {
      width: 120,
      className: "v:top",
      title: "帳號",
      dataIndex: "username",
    },
    {
      width: 255,
      className: "v:top",
      title: "下發地址",
      render(_, record) {
        return (
          <a
            target="_blank"
            href={TransferHelper.getAddressLink(record.option.address)}
          >
            {record.option.address}
          </a>
        );
      },
    },
    {
      width: 100,
      className: "v:top",
      dataIndex: ["option"],
      title: "一般回調",
      render(_, record) {
        const callback = record.option.callback;
        const hasCallBack = callback !== null;

        if (hasCallBack) {
          return (
            <>
              <span className="fg:green-56">已設定</span>
              <Popover content={callback} title="一般回調">
                <Button type="primary">顯示</Button>
              </Popover>
            </>
          );
        } else {
          return <span className="fg:gray-56">未設定</span>;
        }
      },
    },
    {
      width: 100,
      className: "v:top",
      dataIndex: ["option"],
      title: "會員存單回調",
      render(_, record) {
        const callback = record.option.member_deposit_callback;
        const hasCallBack = callback !== null;

        if (hasCallBack) {
          return (
            <>
              <span className="fg:green-56">已設定</span>
              <Popover content={callback} title="會員存單回調">
                <Button type="primary">顯示</Button>
              </Popover>
            </>
          );
        } else {
          return <span className="fg:gray-56">未設定</span>;
        }
      },
    },
    {
      width: 100,
      className: "v:top",
      dataIndex: ["option"],
      title: "會員提單回調",
      render(_, record) {
        const callback = record.option.member_withdraw_callback;
        const hasCallBack = callback !== null;

        if (hasCallBack) {
          return (
            <>
              <span className="fg:green-56">已設定</span>
              <Popover content={callback} title="會員提單回調">
                <Button type="primary">顯示</Button>
              </Popover>
            </>
          );
        } else {
          return <span className="fg:gray-56">未設定</span>;
        }
      },
    },
    {
      width: 140,
      className: "v:top",
      title: "未下發",
      dataIndex: "balances",
      sorter: () => 1,
      render(_, { balances }) {
        return (
          <>
            {balances.map((balance, idx) => {
              return (
                <div key={idx}>
                  <span className="inline-block w:80 t:right">
                    {DecimalHelper.fixed(balance.amount, 2)}
                  </span>
                  <span className="inline-block w:35 t:right">
                    {balance.currency}
                  </span>
                </div>
              );
            })}
          </>
        );
      },
    },
    {
      width: 180,
      className: "v:top",
      title: "已下發",
      dataIndex: "withdrawn",
      sorter: () => 1,
      render() {
        return <div className="t:right"></div>;
      },
    },
    {
      width: 150,
      className: "v:top",
      title: "已收手續費",
      dataIndex: "fee",
      sorter: () => 1,
      render() {
        return <div className="t:right"></div>;
      },
    },
    {
      className: "v:top",
      title: "備註",
      dataIndex: "note",
    },
    {
      width: 90,
      align: "right",
      className: "v:top px:6!:is(td)",
      title: "建立時間",
      key: "created_at",
      sorter: () => 1,
      render(_, { created_at }) {
        return <div>{dayjs.$ldts(created_at)}</div>;
      },
    },
    {
      width: 210,
      title: "狀態",
      key: "status",
      render(_, record) {
        const isTransferable = record.option.address !== null;
        const TransferButton = () => (
          <Button type="primary" className="bg:gold-66">
            下發
          </Button>
        );

        return (
          <>
            <div
              className={cls({
                "fg:primary": record.status === UserStatus.enabled,
                "fg:disabled": record.status === UserStatus.disabled,
              })}
            >
              {record.status === UserStatus.enabled ? "已啟用" : "已停用"}
            </div>
            <div className="flex gap:8">
              <Button danger type="primary">
                {record.status === UserStatus.disabled ? "啟用" : "停用"}
              </Button>
              <Button
                type="primary"
                onClick={() => updateModalRef.current?.open(record)}
              >
                設定
              </Button>
              {isTransferable && <TransferButton />}
            </div>
          </>
        );
      },
    },
  ];

  const getRowClassName = (record: UserRead) => {
    if (record.status === UserStatus.disabled) {
      return "fg:disabled";
    }
    return "";
  };

  return (
    <div className="flex flex:col gap:8">
      <div className="flex jc:end gap:8">
        <CreateModal
          onCreate={handleCreateModalCreate}
          onFail={() => messageApi.error("商戶新增失敗！")}
        />
      </div>

      <Table
        rowKey="id"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={tenants}
        scroll={{ x: "1600px" }}
        rowClassName={getRowClassName}
      ></Table>

      <UpdateModal ref={updateModalRef} onUpdate={handleUpdateModalUpdate} />
    </div>
  );
};
